/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @author Zdenka Valkučáková <zdenka.valkucakova@ki-wi.cz>
 * @author Petr Mikušek <petr.mikusek@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-disable max-lines */
/* eslint-env browser */

import { Address4, Address6 } from 'ip-address';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';

import { never } from '../../../util.type';
import { errorWithLevelWarning } from '../../error/error-with-level';
import { fetchWithTimeout } from '../../fetch/fetch';
import FileSystemType from '../../file/system/file-system-type';
import { isISOString } from '../../iso-string';
import NetworkType from '../../network/network-type';
import StorageTypesParser from '../../session/storage-types-parser';
import CommunicatorOrientation from '../orientation/communicator-orientation';

/**
 * @import { ControlLock } from '../../control/control-lock'
 * @import { ProxyServerInfo } from '../../network'
 * @import NetworkConnection from '../../network/network-connection'
 * @import { MaybeYearDayInterval } from '../../power/power-manager'
 * @import { DriveInfo, ReportInfo } from '../../report'
 * @import { ServerInfo } from '../../server'
 * @import { SessionAttrs } from '../../session/session-attrs'
 * @import { StatusInfoBasic, StorageUsed } from '../../status-info-dialog'
 * @import { NtpInfo } from '../../time'
 */

/**
 * @typedef {Pick<SessionAttrs, 'deviceId' | 'serverUrl' | 'version'>} ISession
 * @typedef {Record<string, string>} StatusInfo TODO #555: (Ne)typový stub.
 */

/**
 * Komunikátor informací o zařízení pro Ki-Wi Server ve formátu Status Info v1.2:
 * https://gitlab.com/ki-wi/server/kiwi-status-info/-/wikis/Specifikace-status-info-v1.0
 * @see {StatusInfo}
 *
 * TODO: Ne všechno co máme k dispozici umíme vrátit.
 * TODO: Možná některé pomocné funkce přemístit do StatusInfo?
 * TODO: Celé ReportInfo je nyní po refaktoringu zbytečné, jelikož předtím byl ReportInfo
 * cca ve stavu 1:1 posílaném na Ki-Wi Server. Problém je, že nyní jsou různé formáty
 * (např. formáty datumů podle specifikace) distribuované do více souborů, což je špatně
 * udržitelné. Jelikož se nyní ReportInfo využívá jen zde, tak by bylo dobré tyto soubory
 * logicky sloučit, aby to bylo na jednom místě a nebylo součastí zbytečného veřejného
 * rozhraní ReportInfo.
 */
export default class CommunicatorReport {
  /**
   * Escapování části klíče, aby neobsahoval oddělovač `.` nebo escapovací znak `\`.
   * Nejprve se nahradí výskyty `\` za `\\` a následně výskyty `.` za `\.`.
   * @private
   * @param {string} key
   * @returns {string}
   */
  static encodeKeyComponent(key) {
    return key.replaceAll('\\', '\\\\').replaceAll('.', '\\.');
  }

  // TODO #626: Refaktorovat na ServerDeviceId.
  /**
   * Odešle informace o zařízení.
   * @param {ReportInfo} reportInfo Informace o zařízení.
   * @param {string} deviceId Identifikátor Ki-Wi zařízení, které chce komunikovat.
   * @param {string} serverUrl URL adresa Ki-Wi Server pro komunikaci.
   * @returns {Promise<void>}
   */
  static fetch(reportInfo, deviceId, serverUrl) {
    return (
      fetchWithTimeout(`${serverUrl}/api/device/${deviceId}/status`, {
        body: JSON.stringify(this.generateReport(reportInfo, serverUrl)),
        headers: { 'Content-type': 'application/json' },
        method: 'POST'
      })
        // Snížení priority logování pro chyby síťového spojení.
        .catch((error) => {
          throw errorWithLevelWarning(error);
        })
        .then((response) => {
          // Snížení priority logování pro chyby ze serveru.
          if (!response.ok) {
            throw errorWithLevelWarning(
              new RangeError(`Invalid HTTP status "${response.status}".`)
            );
          }
        })
    );
  }

  /**
   * Zformátuje procento v rozsahu [0.000, 1.000].
   * @private
   * @param {number | undefined} n
   * @returns {string | undefined} Zformátované procento v rozsahu [0.000, 1.000] nebo undefined,
   * pokud je na vstupu undefined nebo číslo není konečné.
   */
  static formatPercent(n) {
    if (isNil(n) || !Number.isFinite(n)) {
      return undefined;
    }
    return n.toFixed(3);
  }

  /**
   * Vygeneruje základní (zjednodušený) report.
   * @param {ReportInfo} reportInfo
   * @param {ServerInfo | undefined} serverInfo
   * @param {ISession} session
   * @param {Record<'localTimeServerOffset', number | undefined>} heartbeatInfo
   * @returns {StatusInfoBasic}
   */
  static generateBasicReport(
    reportInfo,
    serverInfo,
    session,
    { localTimeServerOffset }
  ) {
    const defaultNetwork = this.selectDefaultNetworkConnectionWithIp(
      reportInfo?.networkConnections ?? []
    );

    const { deviceId, version } = session;

    const local = reportInfo?.nowTimestamp;
    let { contentTimestamp } = reportInfo || undefined;
    if (!isISOString(contentTimestamp)) {
      contentTimestamp = undefined;
    }

    return {
      application: {
        assetPending: reportInfo?.content?.asset?.pending || undefined,
        connection: serverInfo?.connected ?? false,
        id: deviceId || undefined,
        name: reportInfo?.deviceName,
        server: serverInfo?.serverUrl || session.serverUrl || undefined,
        version: version || undefined
      },
      network: {
        adapter: defaultNetwork?.getType(),
        ipAddress: defaultNetwork?.getIp(),
        macAddress: defaultNetwork?.getMac(),
        proxyServerInfo: reportInfo?.proxyServerInfo
      },
      system: {
        firmware: reportInfo?.firmwareVersion,
        manufacturer: reportInfo?.manufacturer,
        model: reportInfo?.modelName,
        serialNumber: reportInfo?.serialNumber,
        volume: reportInfo?.volume,
        storage: {
          used: this.generateStorageForBasic(reportInfo?.driveInfo)
        }
      },
      time: {
        boot: reportInfo?.bootTimestamp,
        contentTimestamp,
        local,
        localTimeServerOffset,
        ntpInfo: reportInfo.ntpInfo,
        zone: reportInfo?.timezone
      }
    };
  }

  /**
   *
   * @param {ReportInfo['capabilities']} capabilities
   * @returns {StatusInfo}
   */
  static generateCapabilities(capabilities) {
    if (isNil(capabilities)) {
      throw new Error(
        'Capabilities must be provided to generate capabilities report.'
      );
    }
    const prefix = 'capabilities';

    return Object.assign(
      {},
      ...Object.entries(capabilities).flatMap(([capability, versions]) =>
        Object.entries(versions).map(([major, minor]) =>
          this.keyValueMandatory(`${prefix}.${capability}.${major}`, minor)
        )
      )
    );
  }

  /**
   * @param {string} keyPrefix Vyescapovaný prefix klíčů.
   * @param {string | undefined} customKey
   * @param {{ [key: string]: string } | undefined} customReport
   * @returns {StatusInfo | {}}
   */
  static generateCustomReport(keyPrefix, customKey, customReport) {
    if (isNil(customKey) || isNil(customReport)) {
      return {};
    }
    return Object.entries(customReport).reduce((acc, [key, value]) => {
      acc[`${keyPrefix}.${customKey}.${key}`] = value;
      return acc;
    }, {});
  }

  // TODO #536: Přidat do specifikace a implementace Status Info API
  // reportování skutečně nastavených prázdnin. Zatím se nepoužívá.
  // TODO: Zvážit, zda raději neserializovat jako jeden klíč než pole,
  // i kvůli problému s nerozlišitelností nezjištěno vs prázdný list.
  /**
   * Vygeneruje Status Info sekci pro skutečně nastavené prázdninové intervaly.
   * @private
   * @param {string} keyPrefix Vyescapovaný prefix klíčů.
   * @param {MaybeYearDayInterval[] | undefined} holidays
   * @returns {StatusInfo | {}}
   */
  static generateHolidays(keyPrefix, holidays) {
    if (isNil(holidays)) {
      return {};
    }
    return holidays.reduce(
      (acc, { start, end }, i) => ({
        ...acc,
        [`${keyPrefix}.${i}`]: `${start}/${end}`
      }),
      {}
    );
  }

  /**
   * Vygeneruje Status Info "memory.*" sekci nebo základ pro "storage.fs.{mountPoint}.*" sekci.
   * @private
   * @param {string} keyPrefix Vyescapovaný prefix klíčů.
   * @param {number | undefined} used použito v B
   * @param {number | undefined} total celkem v B
   * @returns {StatusInfo | {}}
   */
  static generateMemory(keyPrefix, used, total) {
    if (!Number.isFinite(used) || !Number.isFinite(total)) {
      return {};
    }
    const res = {
      ...this.keyValueOptional(`${keyPrefix}.total`, total),
      ...this.keyValueOptional(`${keyPrefix}.used`, used)
    };
    if (total === 0 || isNil(total) || isNil(used)) {
      return res;
    }
    return {
      ...res,
      ...this.keyValueOptional(
        `${keyPrefix}.use`,
        this.formatPercent(used / total)
      )
    };
  }

  /**
   * @private
   * @param {NetworkConnection[]} networkConnections
   * @returns {StatusInfo}
   */
  static generateNetworkAdapters(networkConnections) {
    return networkConnections.reduce((acc, networkConnection) => {
      const adapter = {};
      const adapterPrefix = this.prefixNetworkAdapter(
        networkConnection.getMac()
      );
      // TODO: Kvůli úplnému pokrytí enumu nemůže být null a tudíž zbytečně volitelné.
      const adapterType = this.mapNetworkAdapterType(
        networkConnection.getType()
      );
      if (!isNil(adapterType)) {
        adapter[`${adapterPrefix}.type`] = adapterType;
      }
      // Pozor, getIp může vyhodit výjimku, pokud není IP nastavena, proto test pomocí hasIp!
      if (networkConnection.hasIp()) {
        const ip = networkConnection.getIp();
        try {
          adapter[`${adapterPrefix}.ipv4.ip`] = new Address4(ip).correctForm();
        } catch {}
        try {
          adapter[`${adapterPrefix}.ipv6.ip`] = new Address6(ip).correctForm();
        } catch {}
      }
      return { ...acc, ...adapter };
    }, {});
  }

  /**
   * @private
   * @param {ProxyServerInfo | undefined} proxyServerInfo
   * @returns {StatusInfo | {}}
   */
  static generateNetworkProxyServer(proxyServerInfo) {
    const defaultPrefix = 'network.default';
    return {
      ...this.keyValueOptional(
        `${defaultPrefix}.proxy.enabled`,
        proxyServerInfo?.enabled
      ),
      ...this.keyValueOptional(
        `${defaultPrefix}.proxy.host`,
        proxyServerInfo?.host
      ),
      ...this.keyValueOptional(
        `${defaultPrefix}.proxy.port`,
        proxyServerInfo?.port
      )
    };
  }

  /**
   * Vygeneruje Status Info "network.*" sekci.
   * @private
   * @param {Pick<ReportInfo, 'networkConnections' | 'proxyServerInfo'>} reportInfo
   * @returns {StatusInfo | {}}
   */
  static generateNetwork(reportInfo) {
    const { networkConnections, proxyServerInfo } = reportInfo;
    if (isNil(networkConnections) || networkConnections.length === 0) {
      return {};
    }

    const networkAdapters = this.generateNetworkAdapters(networkConnections);
    const networkProxyServer = this.generateNetworkProxyServer(proxyServerInfo);

    const defaultConnection =
      this.selectDefaultNetworkConnectionWithIp(networkConnections);
    if (isNil(defaultConnection)) {
      return { ...networkAdapters, ...networkProxyServer };
    }

    // Důvěřuj, ale prověřuj, jelikož NetworkConnection může v getIp vyhodit
    // výjimku, pokud by programátorskou chybou v algoritmu nebyla uvedena IP,
    // takže díky volitelnosti default sekce raději vrátíme nyní alespoň něco.
    if (!defaultConnection.hasIp()) {
      return { ...networkAdapters, ...networkProxyServer };
    }

    const mac = defaultConnection.getMac();

    // TODO: Trochu nedomyšlenost specifikace, že dual IP stack zařízení
    // mohou mít dvě výchozí IP adresy, ale stejně NetworkConnection může
    // nyní nést jen jednu IP adresu neurčitého typu, takže to nevadí.
    // Nyní pro jednoduchost vybereme přednostně IPv4 před IPv6. Vždycky
    // musí vrátit nějakou IP, jinak se jedná o programátorský bug,
    // protože výše jsme si vyfiltrovali spojení s IP adresou.
    const adapterPrefix = this.prefixNetworkAdapter(mac);
    const ip =
      networkAdapters[`${adapterPrefix}.ipv4.ip`] ||
      networkAdapters[`${adapterPrefix}.ipv6.ip`];

    // Důvěřuj, ale prověřuj podruhé, pro robustnost...
    if (isNil(ip)) {
      return { ...networkAdapters, ...networkProxyServer };
    }

    // Doplnění sekce "network.default.*" k "network.adapter.*".
    const defaultPrefix = 'network.default';
    return {
      ...networkAdapters,
      ...networkProxyServer,
      [`${defaultPrefix}.ip`]: ip,
      [`${defaultPrefix}.mac`]: mac
    };
  }

  /**
   * @private
   * @param {NtpInfo | undefined} ntpInfo
   * @returns {StatusInfo | {}}
   */
  static generateNtpInfo(ntpInfo) {
    if (isNil(ntpInfo)) {
      return {};
    }

    return {
      ...this.keyValueOptional('time.ntp.enabled', ntpInfo.enabled),
      ...ntpInfo.server.reduce(
        (acc, server, index) => ({
          ...acc,
          ...this.keyValueOptional(
            `time.ntp.server[${index}].host`,
            server?.host
          )
        }),
        {}
      )
    };
  }

  /**
   * Převede všechny informace o zařízení do formátu Status Info.
   * @param {ReportInfo} reportInfo Informace o zařízení.
   * @param {string} serverUrl URL adresa Ki-Wi Serveru pro komunikaci.
   * @returns {StatusInfo} Status Info 1.8
   * @throws {Error} Hodnota nějakého povinného klíče/sekce nebyla k dispozici.
   */
  static generateReport(reportInfo, serverUrl) {
    // TODO: Nesloučit povinné a nepovinné položky/sekce dohromady?
    const { localControlLock, remoteControlLock } = reportInfo;
    return {
      'app.connection.type': 'light',
      ...this.generateContentAsset(reportInfo),
      ...this.keyValueMandatory('app.connection.url', serverUrl),
      'app.type': 'kiwi-player-light',
      ...this.keyValueMandatory(
        'app.type.kiwi-player-light.runtimePlatform',
        reportInfo.runtimePlatform
      ),
      'app.update.type': 'light',
      ...this.keyValueMandatory(
        'app.update.type.light.updatePlatform',
        reportInfo.updatePlatform
      ),
      ...this.keyValueMandatory('time.local', reportInfo.nowTimestamp),
      ...this.generateNtpInfo(reportInfo.ntpInfo),
      ...this.keyValueOptional('time.zone', reportInfo.timezone),
      version: '1.8',

      // Nepovinné položky

      // Sekce app.*
      ...this.keyValueOptional('app.customKey', reportInfo.customKey),
      ...this.keyValueOptional(
        'app.type.kiwi-player-light.signage.firmware',
        reportInfo.firmwareVersion
      ),
      ...this.keyValueOptional(
        'app.update.type.light.buildId',
        reportInfo.buildId
      ),
      // TODO #504: Po refaktoringu zjišťování buildUrl zde navázat.
      // 'app.update.type.light.buildUrl'
      ...this.keyValueOptional('app.update.url', reportInfo.updateUrl),
      ...this.keyValueOptional(
        'app.updateTimestamp',
        reportInfo.contentTimestamp
      ),
      ...this.keyValueOptional('app.version', reportInfo.version),

      // Sekce capabilities.*
      ...this.generateCapabilities(reportInfo.capabilities),

      // Sekce control.*
      ...this.keyValueOptional(
        'control.local.exceptions.power',
        this.isControlPower(localControlLock)
      ),
      ...this.keyValueOptional(
        'control.local.locked',
        this.isControlLocked(localControlLock)
      ),
      ...this.keyValueOptional(
        'control.remote.exceptions.power',
        this.isControlPower(remoteControlLock)
      ),
      ...this.keyValueOptional(
        'control.remote.locked',
        this.isControlLocked(remoteControlLock)
      ),

      // Sekce custom.*
      ...this.generateCustomReport(
        'custom',
        reportInfo.customKey,
        reportInfo.customReport
      ),

      // Sekce memory.*
      ...this.generateMemory('memory', reportInfo.usedRam, reportInfo.totalRam),

      // Sekce network.*
      ...this.generateNetwork(reportInfo),

      // TODO #536: Přidat do specifikace a implementace Status Info API
      // reportování skutečně nastavených prázdnin.
      // ...this.generateHolidays('holidays', reportInfo.holidays),

      // Klíč image (screenshot obrazovky)
      ...this.keyValueOptional('image', reportInfo.image),

      // Sekce screen.*
      ...this.generateScreen(reportInfo),

      // Sekce sensor.temperature.*
      ...this.generateSensorTemperature(reportInfo.temperature),

      // Sekce sound.*
      ...this.keyValueOptional('sound.muted', reportInfo.muted),
      ...this.keyValueOptional(
        'sound.volume',
        this.formatPercent(reportInfo.volume)
      ),

      // Sekce storage.*
      ...this.generateStorage(reportInfo.driveInfo),

      // Sekce system.*
      ...this.keyValueOptional('system.manufacturer', reportInfo.manufacturer),
      ...this.keyValueOptional('system.model', reportInfo.modelName),
      ...this.keyValueOptional('system.serial', reportInfo.serialNumber),

      // Sekce time.*
      ...this.keyValueOptional('time.boot', reportInfo.bootTimestamp)
    };
  }

  /**
   * Vygeneruje Status Info "content.asset.*" sekci.
   * @private
   * @param {Pick<ReportInfo, 'content'>} reportInfo
   * @returns {StatusInfo|{}}
   */
  static generateContentAsset(reportInfo) {
    if (!reportInfo.content) {
      return {};
    }
    const media = reportInfo.content.asset.media.reduce((acc, m) => {
      acc[`app.content.asset.pending.${m.id}.name`] = m.name;
      return acc;
    }, {});
    return {
      ...this.keyValueOptional(
        'app.content.asset.pending',
        reportInfo.content.asset.pending
      ),
      ...media
    };
  }

  /**
   * Vygeneruje Status Info "screen.*" sekci.
   * @private
   * @param {ReportInfo} reportInfo
   * @returns {StatusInfo|{}}
   */
  static generateScreen(reportInfo) {
    let resolution;
    if (!isNil(reportInfo.resolution)) {
      const { width, height } = reportInfo.resolution;
      resolution = `${width}x${height}`;
    }
    // Vrací čísla ve shodě se specifikací, takže nemusíme mapovat.
    const orientation = CommunicatorOrientation.generate(
      reportInfo.orientationEnum
    );
    const screenPrefix = 'screen.0';
    return {
      ...this.keyValueOptional(
        `${screenPrefix}.backlight`,
        this.formatPercent(reportInfo.backlight)
      ),
      ...this.keyValueOptional(`${screenPrefix}.orientation`, orientation),
      ...this.keyValueOptional(`${screenPrefix}.primary`, true),
      ...this.keyValueOptional(`${screenPrefix}.resolution`, resolution),
      ...this.keyValueOptional(
        `${screenPrefix}.scaleFactor`,
        this.formatPercent(reportInfo.scaleFactor)
      )
    };
  }

  /**
   * Vygeneruje Status Info "sensor.temperature.*" sekci.
   * @private
   * @param {Celsius | undefined} temperature
   * @returns {StatusInfo | {}}
   */
  static generateSensorTemperature(temperature) {
    if (isNil(temperature)) {
      return {};
    }
    // Máme 1 senzor teploty, proto pevný index 0.
    const keyPrefix = 'sensor.temperature.0';
    return {
      ...this.keyValueOptional(`${keyPrefix}.value`, temperature),
      [`${keyPrefix}.type`]: 'system'
    };
  }

  /**
   * Vygeneruje Status Info "storage.*" sekci.
   * @private
   * @param {DriveInfo | undefined} driveInfo
   * @returns {StatusInfo | {}}
   */
  static generateStorage(driveInfo) {
    if (isNil(driveInfo)) {
      return {};
    }
    return driveInfo.fileSystemUnitInfoArray.reduce(
      (acc, fileSystemUnit) => {
        const keyPrefix = `storage.fs.${this.encodeKeyComponent(
          fileSystemUnit.id
        )}`;
        if (!isNil(fileSystemUnit.type)) {
          return {
            ...acc,
            ...this.generateMemory(
              keyPrefix,
              fileSystemUnit.used,
              fileSystemUnit.total
            ),
            // TODO: Kvůli úplnému pokrytí enumu nemůže být null a tudíž zbytečně volitelné.
            ...this.keyValueOptional(
              `${keyPrefix}.type`,
              this.mapStorageFsType(fileSystemUnit.type)
            )
          };
        }
        return {
          ...acc,
          ...this.generateMemory(
            keyPrefix,
            fileSystemUnit.used,
            fileSystemUnit.total
          )
        };
      },
      {
        ...this.keyValueOptional(
          'storage.types',
          // Naparsuje storage string do a následně výsledek převede na řetězec.
          // To se dělá kvůli tomu, aby se odeslaly pouze validní hodnoty.
          StorageTypesParser.toString(StorageTypesParser.parse(driveInfo.type))
        )
      }
    );
  }

  /**
   * Vygeneruje informace u úložištích ve formátu pro "basic" report.
   * @param {DriveInfo | undefined} driveInfo
   * @returns {StorageUsed}
   */
  static generateStorageForBasic(driveInfo) {
    if (isNil(driveInfo)) {
      return [];
    }

    return driveInfo.fileSystemUnitInfoArray
      .filter(
        ({ used, total }) =>
          Number.isFinite(used) &&
          Number.isFinite(total) &&
          total > 0 &&
          total >= used
      )
      .toSorted((a, b) => b.total - a.total)
      .slice(0, 3)
      .map(({ id, total, used }) => {
        const type = this.encodeKeyComponent(id)
          .replace(/^usb:/, 'usb')
          .replace(/^sdcard:/, 'sdcard');
        return [type, used / total];
      });
  }

  /**
   * Převede zámek ovládání do příznaku `locked`, který se posílá na server.
   * Pokud zámek chybí tak do reportu nevloží nic (např. zařízení nepodporuje).
   * Pokud je zámek přítomný (zařízení omezení podporuje), tak je vždy `locked`
   * nastaveno na `true` nebo `false` dle stavu uzamčení.
   * @private
   * @param {ControlLock | undefined} controlLock
   * @returns {boolean | undefined}
   */
  static isControlLocked(controlLock) {
    return get(controlLock, 'locked', undefined);
  }

  /**
   * Převede zámek ovládání do příznaku `power`, který se posílá na server.
   * Příznak `power` se odesílá pouze v případě, že je nastaven na `true`.
   * @private
   * @param {ControlLock | undefined} controlLock
   * @returns {boolean | undefined}
   */
  static isControlPower(controlLock) {
    const power = get(controlLock, 'exceptions.power');
    if (power) {
      return true;
    }
    return undefined;
  }

  /**
   * Vytvoří objekt s klíčem key a primitivní hodnotou zformátovanou pomocí JSON.stringify(value)
   * nebo vrátí prázdný objekt, pokud je value null nebo undefined.
   * @private
   * @param {string} key
   * @param {boolean | number | string | null | undefined} value
   * @returns {StatusInfo | {}} Objekt s jedním klíčem a string hodnotou nebo prázdný objekt.
   */
  static keyValueOptional(key, value) {
    if (isNil(value)) {
      return {};
    }
    let stringValue = value;
    if (!isString(value)) {
      stringValue = JSON.stringify(value);
    }
    return { [`${key}`]: stringValue };
  }

  /**
   * Vytvoří objekt s klíčem key a primitivní hodnotou zformátovanou pomocí JSON.stringify(value)
   * nebo vyhodí výjimku, pokud je value null nebo undefined.
   * @private
   * @param {string} key
   * @param {boolean | number | string | null | undefined} value
   * @returns {StatusInfo} Objekt s jedním klíčem a string hodnotou.
   * @throws {Error} Pokud je value null nebo undefined.
   */
  static keyValueMandatory(key, value) {
    if (isNil(value)) {
      throw new Error(`Invalid nullish value for mandatory item "${key}"`);
    }
    return this.keyValueOptional(key, value);
  }

  // TODO: Nevracet null kvůli záruce úplného pokrytí enumu.
  /**
   * Namapuje NetworkType na hodnoty podle specifikace pro network.adapter.{mac}.type.
   * @private
   * @param {NetworkType} networkType
   * @returns {string | undefined}
   */
  static mapNetworkAdapterType(networkType) {
    switch (networkType) {
      case NetworkType.Ethernet:
        return 'wired';
      case NetworkType.Wifi:
        return 'wifi';
      default:
        never(networkType);
        return undefined;
    }
  }

  // TODO: Nevracet null kvůli záruce úplného pokrytí enumu.
  /**
   * Namapuje FileSystemUnit na hodnoty podle specifikace pro storage.fs.{mountPoint}.type.
   * @private
   * @param {FileSystemType} fileSystemType
   * @returns {string | undefined}
   */
  static mapStorageFsType(fileSystemType) {
    switch (fileSystemType) {
      case FileSystemType.Internal:
        return 'internal';
      case FileSystemType.Usb:
        return 'external';
      case FileSystemType.SdCard:
        return 'external';
      // TODO: Ve specifikaci je ještě 'network', ale pro to nemáme hodnotu pro FileSystemType.
      default:
        never(fileSystemType);
        return undefined;
    }
  }

  /**
   * TODO #506: Pozor, že MAC adresa z NetworkConnection
   * nyní nemusí být normalizovaná, ale předpokládáme, že má potřebný
   * formát lower-case po dvojicích oddělené dvojtečkou, jak vyžaduje
   * specifikace Status Info.
   * @private
   * @param {string} mac
   * @returns {string} Prefix klíče pro daný adaptér ve Status Info.
   */
  static prefixNetworkAdapter(mac) {
    return `network.adapter.${this.encodeKeyComponent(mac)}`;
  }

  /**
   * Vybere maximálně jeden síťový adaptér s IP adresou.
   * @private
   * @param {NetworkConnection[]} networkConnections
   * @returns {NetworkConnection | undefined}
   */
  static selectDefaultNetworkConnectionWithIp(networkConnections) {
    const withIpConnections = networkConnections.filter((networkConnection) =>
      networkConnection.hasIp()
    );

    // Sice alibismus, ale při volitelnosti default sekce a nemožnosti určení
    // výchozí IP bez znalosti routovacích tabulek s metrikami je to vlastně
    // správné vracet default sekci jen při právě jednom adaptéru s IP adresou.
    if (withIpConnections.length === 1) {
      return withIpConnections[0];
    }
    return undefined;
  }
}
